import { useMutation, useQuery, useQueryClient } from "react-query";
import { useApiService } from "../useApiService";
import {
  MapStateControllerService,
  VastuControllerService,
  VastuSelectionControllerService,
} from "../../services";
import { VastuRequest } from "../../types/VastuRemedy";
import { QUERY } from "../../constants/query";

// Hook to check Vastu remedy for a property
export const useCheckVastuRemedy = () => {
  const { handleRequest } = useApiService();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    ({
      requestBody,
      propertyId,
    }: {
      requestBody: VastuRequest; // Use the VastuRequest type here
      propertyId?: string;
    }) =>
      handleRequest(
        VastuControllerService.vastuControllerCheckVastuRemedy(
          requestBody,
          propertyId
        )
      ),
    {
      onSuccess: () => {
        console.log("Vastu remedy generated.");
        queryClient.invalidateQueries(QUERY.GET_VASTU_SELECTION);
        queryClient.invalidateQueries(QUERY.GET_REMEDY);
      },
    }
  );

  return {
    ...mutation,
  };
};

export const useGetRemedy = (remedyId: string) => {
  const { handleRequest } = useApiService();
  const queryKey = [QUERY.GET_REMEDY, remedyId];

  const query = useQuery(
    queryKey,
    () =>
      handleRequest(
        VastuControllerService.vastuControllerGetRemedyById(remedyId)
      ),
    {
      enabled: !!remedyId,
    }
  );

  return {
    ...query,
  };
};

export const useGetVastuSelection = (propertyId: string) => {
  const { handleRequest } = useApiService();
  const queryKey = [QUERY.GET_VASTU_SELECTION, propertyId];

  const query = useQuery(
    queryKey,
    () =>
      handleRequest(
        VastuSelectionControllerService.vastuSelectionControllerGetVastuSelection(
          propertyId
          // tenantId
        )
      ),
    {
      enabled: !propertyId || !!propertyId,
    }
  );

  return {
    ...query,
  };
};

export const useEditVastuRemedy = () => {
  const { handleRequest } = useApiService();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    ({
      remedyId,
      field,
      requestBody,
    }: {
      remedyId: string;
      field?: string;
      requestBody?: {
        /**
         * Updated content for the field
         */
        content: Record<string, string | string[] | Record<string, any>>;
      };
    }) =>
      handleRequest(
        VastuControllerService.vastuControllerEditRemedy(
          remedyId,
          field,
          requestBody
        )
      ),
    {
      onSuccess: () => {
        console.log("Remedy edited successfully");
        // Invalidate or refetch any relevant queries here
        queryClient.invalidateQueries(QUERY.GET_REMEDY);
      },
      onError: (error) => {
        console.error("Error editing remedy:", error);
      },
    }
  );

  return {
    ...mutation,
  };
};

export const useGetMapState = (
  propertyId: string,
  context: "map" | "siteplan"
) => {
  const { handleRequest } = useApiService();
  const queryKey = [QUERY.GET_MAP_STATE, propertyId, context];

  const query = useQuery(
    queryKey,
    () =>
      handleRequest(
        MapStateControllerService.mapStateControllerGetMapState(
          propertyId,
          context
        )
      ),
    {
      enabled: !!propertyId && !!context,
    }
  );

  return { ...query };
};

export const useCreateMapState = () => {
  const { handleRequest } = useApiService();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    ({
      requestBody,
      propertyId,
      tenantId,
      context,
    }: {
      requestBody: {
        pins?: Array<Record<string, any>>;
        droppedItems?: Array<Record<string, any>>;
        sitePlanPoints?: Array<Record<string, any>>;
        mapPoints?: Array<Record<string, any>>;
      };
      propertyId?: string;
      tenantId?: string;
      context: "map" | "siteplan";
    }) =>
      handleRequest(
        MapStateControllerService.mapStateControllerCreateMapState(
          { ...requestBody, context },
          propertyId,
          tenantId
        )
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY.GET_MAP_STATE);
      },
    }
  );

  return { ...mutation };
};

export const useClearMapState = () => {
  const { handleRequest } = useApiService();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    ({
      propertyId,
      context,
    }: {
      propertyId: string;
      context: "map" | "siteplan";
    }) =>
      handleRequest(
        MapStateControllerService.mapStateControllerClearMapState(
          propertyId,
          context
        )
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY.GET_MAP_STATE);
      },
    }
  );

  return { ...mutation };
};
